import { db } from "../firebase";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  updateDoc,
} from "firebase/firestore";
import { langNgheFirestore } from "../lib/langNgheFirestore";

const hopTacXaCol = () => collection(db, "hopTacXa");

// Bộ đếm STT cho Mã HTX: 1 document duy nhất, đếm TOÀN HỆ THỐNG (không tách
// theo tỉnh/xã như boDemMaChuVuon) nên không cần khoá động — xem SO_CHU_SO_STT.
const boDemDoc = () => doc(db, "boDem", "hopTacXa");

const SO_CHU_SO_STT = 3;

// Giống Mã chủ vườn: dùng mã hành chính (tinhMa/xaMa từ ChonDiaChi), không
// slug tên — tránh mã dài kiểu "HTX-TinhSonLa-PhuongChiengSinh-001".
export function ghepMaHopTacXa({ tinhMa, xaMa, stt }) {
  const sttChuoi = String(stt).padStart(SO_CHU_SO_STT, "0");
  return `HTX-${String(tinhMa)}-${String(xaMa)}-${sttChuoi}`;
}

// Document tạo trước khi có mã định danh nghiệp vụ không có maHopTacXa/tinh/xa.
// Chuẩn hoá lúc ĐỌC (không migrate dữ liệu production) để mọi nơi dùng chỉ
// thấy đúng 1 hình dạng: field thiếu = chuỗi rỗng, không phải undefined.
function chuanHoa(id, data) {
  return {
    id,
    tenHopTacXa: data.tenHopTacXa ?? "",
    nguoiDaiDien: data.nguoiDaiDien ?? "",
    sdt: data.sdt ?? "",
    diaChi: data.diaChi ?? "",
    email: data.email ?? "",
    tinh: data.tinh ?? "",
    xa: data.xa ?? "",
    tinhMa: data.tinhMa ?? "",
    xaMa: data.xaMa ?? "",
    maHopTacXa: data.maHopTacXa ?? "",
    createdAt: data.createdAt ?? "",
  };
}

export const hopTacXaApi = {
  async danhSachTatCa() {
    const snap = await getDocs(hopTacXaCol());
    return snap.docs.map((d) => chuanHoa(d.id, d.data()));
  },

  langNgheDanhSach(onChange, onError) {
    return langNgheFirestore(hopTacXaCol(), {
      mapAsync: (raw) => (raw ?? []).map((d) => chuanHoa(d.id, d)),
      onChange,
      onError,
    });
  },

  async chiTiet(id) {
    const snap = await getDoc(doc(db, "hopTacXa", id));
    if (!snap.exists()) throw new Error("Không tìm thấy hợp tác xã");
    return chuanHoa(snap.id, snap.data());
  },

  // Tỉnh/Xã (kèm mã hành chính) bắt buộc vì là thành phần của Mã HTX — thà
  // báo lỗi rõ ràng còn hơn sinh mã cụt rồi vĩnh viễn không sửa lại được.
  async taoMoi({ tenHopTacXa, nguoiDaiDien, sdt, diaChi, email, tinh, xa, tinhMa, xaMa }) {
    const maTinh = String(tinhMa ?? "").trim();
    const maXa = String(xaMa ?? "").trim();
    if (!maTinh || !maXa) {
      throw new Error("Vui lòng chọn Tỉnh và Xã để sinh Mã HTX");
    }

    // Tăng bộ đếm + đặt chỗ maHopTacXa/{ma} + tạo HTX trong CÙNG 1
    // transaction: nếu 1 trong 3 lỗi thì cả 3 cùng huỷ. Reservation
    // create-only (rules không cho update/delete) chặn cấp trùng mã kể cả
    // khi boDem/hopTacXa bị reset trên Console.
    return runTransaction(db, async (tx) => {
      const refBoDem = boDemDoc();
      const snapBoDem = await tx.get(refBoDem);
      const stt = (snapBoDem.exists() ? snapBoDem.data().soHienTai ?? 0 : 0) + 1;
      const maHopTacXa = ghepMaHopTacXa({ tinhMa: maTinh, xaMa: maXa, stt });

      const refMa = doc(db, "maHopTacXa", maHopTacXa);
      const snapMa = await tx.get(refMa);
      if (snapMa.exists()) {
        throw new Error(`Mã HTX ${maHopTacXa} đã được cấp. Kiểm tra bộ đếm boDem/hopTacXa.`);
      }

      const createdAt = new Date().toISOString();
      const data = {
        tenHopTacXa,
        nguoiDaiDien: nguoiDaiDien ?? "",
        sdt: sdt ?? "",
        diaChi: diaChi ?? "",
        email: email ?? "",
        tinh: tinh ?? "",
        xa: xa ?? "",
        tinhMa: maTinh,
        xaMa: maXa,
        maHopTacXa,
        createdAt,
      };

      const refHtx = doc(hopTacXaCol());
      tx.set(refBoDem, { soHienTai: stt });
      tx.set(refMa, { hopTacXaId: refHtx.id, createdAt });
      tx.set(refHtx, data);
      return chuanHoa(refHtx.id, data);
    });
  },

  // maHopTacXa KHÔNG nằm trong danh sách field ghi ở đây, kể cả khi caller
  // truyền vào: mã sinh 1 lần lúc tạo và giữ nguyên trọn đời, đổi Tỉnh/Xã sau
  // này cũng không sinh lại (nếu sinh lại thì mã cũ đã in trên chứng từ sẽ
  // không còn khớp dữ liệu).
  async capNhat(id, { tenHopTacXa, nguoiDaiDien, sdt, diaChi, email, tinh, xa, tinhMa, xaMa }) {
    await updateDoc(doc(db, "hopTacXa", id), {
      tenHopTacXa,
      nguoiDaiDien: nguoiDaiDien ?? "",
      sdt: sdt ?? "",
      diaChi: diaChi ?? "",
      email: email ?? "",
      tinh: tinh ?? "",
      xa: xa ?? "",
      tinhMa: String(tinhMa ?? "").trim(),
      xaMa: String(xaMa ?? "").trim(),
    });
    return hopTacXaApi.chiTiet(id);
  },

  // Cấp mã cho HTX tạo TRƯỚC khi có tính năng Mã HTX (document chưa từng có
  // field maHopTacXa). Không dùng để cấp lại mã cho HTX đã có mã — rules chỉ
  // cho ghi maHopTacXa khi field này chưa tồn tại trên document (xem
  // firestore.rules), nên gọi lại capMa cho HTX đã có mã sẽ bị chặn ở cả 2
  // tầng (throw ở đây trước khi tới Firestore, và permission-denied nếu race).
  async capMa(id) {
    return runTransaction(db, async (tx) => {
      const refHtx = doc(db, "hopTacXa", id);
      const snapHtx = await tx.get(refHtx);
      if (!snapHtx.exists()) throw new Error("Không tìm thấy hợp tác xã");

      const data = snapHtx.data();
      if (data.maHopTacXa) throw new Error("Hợp tác xã này đã có mã");

      const tinhMa = String(data.tinhMa ?? "").trim();
      const xaMa = String(data.xaMa ?? "").trim();
      if (!tinhMa || !xaMa) {
        throw new Error("Chọn lại Tỉnh và Xã (bấm Sửa) trước khi cấp mã");
      }

      const refBoDem = boDemDoc();
      const snapBoDem = await tx.get(refBoDem);
      const stt = (snapBoDem.exists() ? snapBoDem.data().soHienTai ?? 0 : 0) + 1;
      const maHopTacXa = ghepMaHopTacXa({ tinhMa, xaMa, stt });

      const refMa = doc(db, "maHopTacXa", maHopTacXa);
      const snapMa = await tx.get(refMa);
      if (snapMa.exists()) {
        throw new Error(`Mã HTX ${maHopTacXa} đã được cấp. Kiểm tra bộ đếm boDem/hopTacXa.`);
      }

      const createdAt = new Date().toISOString();
      tx.set(refBoDem, { soHienTai: stt });
      tx.set(refMa, { hopTacXaId: id, createdAt });
      tx.update(refHtx, { maHopTacXa });
      return chuanHoa(id, { ...data, maHopTacXa });
    });
  },

  async xoa(id) {
    await deleteDoc(doc(db, "hopTacXa", id));
  },
};
